import {
  Alert,
  Button,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Snackbar,
  TextField,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  DialogTitle,
  Dialog,
  DialogActions,
  DialogContent,
  Tab,
  Tabs,
  Stepper,
  Step,
  StepLabel,
  StepButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useState, useEffect } from "react";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  FileText,
  Trash2,
  Upload,
  X,
  Plane,
  BadgeCheck,
  HeartPulse,
  CheckCircle2,
  ChevronRight,
  ChevronLeft,
} from "lucide-react";
import Select from "../../../components/common/Select";
import { RequiredAsterisk } from "../../../components/professional/RequiredAsterisk";
import { formatDateToYMD, formatISOToDate } from "../../../utils/helpers";
import { MAX_FILESIZE_MB, SIZE_KB } from "../../../constants";
// import { aviationCategoryOptions } from "../../../utils/professionalDropdownOptions";


const createInitialExperience = (data = {}) => ({
  id: data?.id || null,
  pilot_type: data?.pilot_type || "",
  other_pilot_type: data?.other_pilot_type || "",
  aviation_category: data?.aviation_category || "",
  other_aviation_category: data?.other_aviation_category || "",
  years_of_experience: data?.years_of_experience || "",
  aircraft_type: data?.aircraft_type || "",
  other_aircraft_type: data?.other_aircraft_type || "",
  pilot_in_command_hours: data?.pilot_in_command_hours || 0,
  first_officer_hours: data?.first_officer_hours || 0,
  dual_hours: data?.dual_hours || 0,
  total_hours: data?.total_hours || 0,
  last_flight_date: data?.last_flight_date || "",
  additional_details: data?.additional_details || "",
});

const createInitialLicense = (data = {}) => {
  let licenseStatus = data?.license_status || "";
  if (data?.expiry_date && licenseStatus !== "expired") {
    const expiry = new Date(data.expiry_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (expiry < today) licenseStatus = "expired";
  }
  return {
    id: data?.license_id || data?.id || null,
    license_upload: data?.license_upload || null,
    license_type: data?.license_type || "",
    issue_authority: data?.issue_authority || "",
    license_number: data?.license_number || "",
    issue_date: data?.issue_date || null,
    license_rating: data?.license_rating || "",
    expiry_date: data?.expiry_date || null,
    license_status: licenseStatus,
    renewal_date: data?.renewal_date || null,
  };
};

const createInitialMedical = (data = {}) => {
  let medicalStatus = data?.medical_status || "";
  if (data?.medical_expiry_date && medicalStatus !== "expired") {
    const expiry = new Date(data.medical_expiry_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (expiry < today) medicalStatus = "expired";
  }
  return {
    id: data?.license_id || data?.id || null,
    medical_upload: data?.medical_upload || null,
    medical_class: data?.medical_class || "",
    medical_certificate_number: data?.medical_certificate_number || "",
    medical_issue_date: data?.medical_issue_date || null,
    medical_expiry_date: data?.medical_expiry_date || null,
    medical_renewal_date: data?.medical_renewal_date || null,
    medical_status: medicalStatus,
  };
};



const DeleteDialog = ({ open, onClose, onConfirm, label = "entry" }) => (
  <Dialog open={open} onClose={onClose}>
    <DialogTitle>Delete {label}</DialogTitle>
    <DialogContent>
      <Typography>
        Are you sure you want to delete this {label.toLowerCase()}? This action
        cannot be undone.
      </Typography>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Cancel</Button>
      <Button color="error" variant="contained" onClick={onConfirm}>
        Delete
      </Button>
    </DialogActions>
  </Dialog>
);


const SavedBanner = ({ show }) =>
  show ? (
    <Box
      display="flex"
      alignItems="center"
      gap={1}
      mb={2}
      px={2}
      py={1.2}
      sx={{
        bgcolor: "success.50",
        border: "1px solid",
        borderColor: "success.200",
        borderRadius: 2,
      }}
    >
      <CheckCircle2 size={16} className="text-green-600" />
      <Typography variant="body2" color="success.main" fontWeight={500}>
        Saved successfully
      </Typography>
    </Box>
  ) : null;


const TabPanel = ({ children, value, index }) => (
  <Box
    role="tabpanel"
    hidden={value !== index}
    id={`experience-tabpanel-${index}`}
    aria-labelledby={`experience-step-${index}`}
    sx={{ display: value === index ? "block" : "none", pt: 1 }}
  >
    {children}
  </Box>
);



const ExperienceTab = ({ initial, onSubmit, onCancel, dropDownOptions, onNext }) => {
  const { aircraftTypeChoices, pilotTypes, aviationCategoryChoices } = dropDownOptions;

  const pilotTypesList = (pilotTypes || []).some((c) => c.value === "other")
    ? pilotTypes
    : [...(pilotTypes || []), { value: "other", label: "Other" }];

  const aircraftTypesList = (aircraftTypeChoices || []).some((c) => c.value === "other")
    ? aircraftTypeChoices
    : [...(aircraftTypeChoices || []), { value: "other", label: "Other" }];

  const [entries, setEntries] = useState([createInitialExperience(initial)]);
  const [expanded, setExpanded] = useState(0);
  const [errors, setErrors] = useState({});
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [saved, setSaved] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setEntries([createInitialExperience(initial)]);
    setExpanded(0);
    setErrors({});
  }, [initial]);

  const handleChange = (index, field, value) => {
    setEntries((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
    setErrors((prev) => {
      if (!prev[`${index}-${field}`]) return prev;
      const next = { ...prev };
      delete next[`${index}-${field}`];
      return next;
    });
  };

  const validate = () => {
    const newErrors = {};
    entries.forEach((exp, i) => {
      if (exp.aviation_category === 'pilot' && !exp.pilot_type) newErrors[`${i}-pilot_type`] = "Pilot type required";
      if (exp.pilot_type === "other" && !exp.other_pilot_type?.trim())
        newErrors[`${i}-other_pilot_type`] = "Please specify pilot type";
      if (!exp.aviation_category)
        newErrors[`${i}-aviation_category`] = "Category required";
      if (exp.aviation_category === "other" && !exp.other_aviation_category)
        newErrors[`${i}-other_aviation_category`] = "Please specify category";
      if (exp.aircraft_type === "other" && !exp.other_aircraft_type?.trim())
        newErrors[`${i}-other_aircraft_type`] = "Please specify aircraft type";
      if (!exp.years_of_experience)
        newErrors[`${i}-years_of_experience`] = "Required";
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!validate()) return;

    const enriched = entries.map((exp) => {
      const isEngineer =
        exp.aviation_category === "engineer" || exp.pilot_type === "engineer";

      // Start with base fields every category needs
      const base = {
        id: exp.id,
        aviation_category: exp.aviation_category,
        other_aviation_category:
          exp.aviation_category === "other" ? exp.other_aviation_category : undefined,
        years_of_experience: exp.years_of_experience,
        aircraft_type: exp.aircraft_type === "other" ? (exp.other_aircraft_type?.trim() || "other") : exp.aircraft_type,
        additional_details: exp.additional_details,
      };

      // Pilot-type field — not needed for engineers
      if (!isEngineer) {
        base.pilot_type = exp.pilot_type === "other" ? (exp.other_pilot_type?.trim() || "other") : exp.pilot_type;
      }

      // Flight-hours fields — only for non-engineers
      if (!isEngineer) {
        const pic = Number(exp.pilot_in_command_hours) || 0;
        const fo = Number(exp.first_officer_hours) || 0;
        const dual = Number(exp.dual_hours) || 0;

        base.pilot_in_command_hours = pic;
        base.first_officer_hours = fo;
        base.dual_hours = dual;
        base.total_hours = pic + fo + dual;
        base.last_flight_date = exp.last_flight_date || null;
      }

      // Strip undefined keys so they don't appear in the JSON body at all
      return Object.fromEntries(
        Object.entries(base).filter(([, v]) => v !== undefined)
      );
    });

    if (onSubmit) {
      setIsSubmitting(true);
      try {
        const shouldClose = !onNext;
        const savedData = await onSubmit({ experience: enriched }, shouldClose);
        if (savedData) {
          if (Array.isArray(savedData)) {
            setEntries((prev) =>
              prev.map((entry, idx) => ({
                ...entry,
                id: savedData[idx]?.id || entry.id,
              }))
            );
          }
          setSaved(true);
          setTimeout(() => setSaved(false), 3000);
          if (onNext) {
            onNext();
          }
        }
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleDelete = () => {
    if (deleteIndex === null || entries.length === 1) {
      setDeleteIndex(null);
      return;
    }
    const updated = entries.filter((_, i) => i !== deleteIndex);
    setEntries(updated);
    const cleanErrors = {};
    Object.keys(errors).forEach((k) => {
      if (!k.startsWith(`${deleteIndex}-`)) cleanErrors[k] = errors[k];
    });
    setErrors(cleanErrors);
    if (expanded === deleteIndex) setExpanded(false);
    else if (expanded > deleteIndex) setExpanded(expanded - 1);
    setDeleteIndex(null);
  };

  return (
    <form onSubmit={handleSubmit}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <SavedBanner show={saved} />
        {entries.map((exp, index) => {
          const totalHours =
            Number(exp.pilot_in_command_hours) +
            Number(exp.first_officer_hours) +
            Number(exp.dual_hours);
          const isPilot = exp.aviation_category === 'pilot'
          return (
            <Accordion
              key={index}
              expanded={expanded === index}
              onChange={() =>
                setExpanded(expanded === index ? false : index)
              }
              sx={{ mb: 2 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  width="100%"
                  pr={1}
                >
                  <Typography fontWeight="bold">
                    Experience {index + 1}
                  </Typography>
                  {entries.length > 1 && (
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteIndex(index);
                      }}
                    >
                      <Trash2 size={18} />
                    </IconButton>
                  )}
                </Box>
              </AccordionSummary>

              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Select
                      label={
                        <span>
                          Aviation Category <RequiredAsterisk />
                        </span>
                      }
                      options={aviationCategoryChoices}
                      value={exp.aviation_category}
                      onChange={(v) =>
                        handleChange(index, "aviation_category", v)
                      }
                      error={!!errors[`${index}-aviation_category`]}
                      helperText={errors[`${index}-aviation_category`]}
                    />
                  </Grid>

                  {exp.aviation_category === "other" && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label={
                          <span>
                            Specify Category <RequiredAsterisk />
                          </span>
                        }
                        fullWidth
                        value={exp.other_aviation_category}
                        onChange={(e) =>
                          handleChange(
                            index,
                            "other_aviation_category",
                            e.target.value
                          )
                        }
                        error={!!errors[`${index}-other_aviation_category`]}
                        helperText={errors[`${index}-other_aviation_category`]}
                      />
                    </Grid>
                  )}

                  {exp.aviation_category !== "engineer" && (
                    <>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Select
                          label={
                            <span>
                              Pilot Type <RequiredAsterisk />
                            </span>
                          }
                          options={pilotTypesList}
                          value={exp.pilot_type}
                          onChange={(v) => handleChange(index, "pilot_type", v)}
                          error={!!errors[`${index}-pilot_type`]}
                          helperText={errors[`${index}-pilot_type`]}
                        />
                      </Grid>

                      {exp.pilot_type === "other" && (
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            label={
                              <span>
                                Specify Pilot Type <RequiredAsterisk />
                              </span>
                            }
                            fullWidth
                            value={exp.other_pilot_type || ""}
                            onChange={(e) =>
                              handleChange(index, "other_pilot_type", e.target.value)
                            }
                            error={!!errors[`${index}-other_pilot_type`]}
                            helperText={errors[`${index}-other_pilot_type`]}
                          />
                        </Grid>
                      )}
                    </>
                  )}

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      type="number"
                      fullWidth
                      slotProps={{ htmlInput: { min: 0 } }}
                      value={exp.years_of_experience}
                      onChange={(e) =>
                        handleChange(
                          index,
                          "years_of_experience",
                          e.target.value
                        )
                      }
                      error={!!errors[`${index}-years_of_experience`]}
                      helperText={errors[`${index}-years_of_experience`]}
                      label={
                        <span>
                          Years of Experience <RequiredAsterisk />
                        </span>
                      }
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Select
                      label="Aircraft Type"
                      options={aircraftTypesList}
                      value={exp.aircraft_type}
                      onChange={(value) =>
                        handleChange(index, "aircraft_type", value)
                      }
                      limitTags={3}
                      error={!!errors[`${index}-aircraft_type`]}
                      helperText={errors[`${index}-aircraft_type`]}
                    />
                  </Grid>

                  {exp.aircraft_type === "other" && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label={
                          <span>
                            Specify Aircraft Type <RequiredAsterisk />
                          </span>
                        }
                        fullWidth
                        value={exp.other_aircraft_type || ""}
                        onChange={(e) =>
                          handleChange(index, "other_aircraft_type", e.target.value)
                        }
                        error={!!errors[`${index}-other_aircraft_type`]}
                        helperText={errors[`${index}-other_aircraft_type`]}
                      />
                    </Grid>
                  )}

                  {/* Only show pilot hours fields for non-engineer types */}
                  {exp.pilot_type !== "engineer" && exp.aviation_category !== "engineer" && (
                    <>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          type="number"
                          fullWidth
                          slotProps={{ htmlInput: { min: 0 } }}
                          value={exp.pilot_in_command_hours}
                          onChange={(e) =>
                            handleChange(
                              index,
                              "pilot_in_command_hours",
                              e.target.value
                            )
                          }
                          error={!!errors[`${index}-pilot_in_command_hours`]}
                          helperText={errors[`${index}-pilot_in_command_hours`]}
                          label="Pilot-in-Command Hours"
                        />
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          type="number"
                          fullWidth
                          slotProps={{ htmlInput: { min: 0 } }}
                          value={exp.first_officer_hours}
                          onChange={(e) =>
                            handleChange(
                              index,
                              "first_officer_hours",
                              e.target.value
                            )
                          }
                          error={!!errors[`${index}-first_officer_hours`]}
                          helperText={errors[`${index}-first_officer_hours`]}
                          label="First Officer Hours"
                        />
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          type="number"
                          fullWidth
                          slotProps={{ htmlInput: { min: 0 } }}
                          value={exp.dual_hours}
                          onChange={(e) =>
                            handleChange(index, "dual_hours", e.target.value)
                          }
                          error={!!errors[`${index}-dual_hours`]}
                          helperText={errors[`${index}-dual_hours`]}
                          label="Dual Hours"
                        />
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          label="Total Flight Hours"
                          fullWidth
                          value={totalHours}
                          disabled
                        />
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          type="date"
                          fullWidth
                          slotProps={{ inputLabel: { shrink: true } }}
                          value={exp.last_flight_date}
                          onChange={(e) =>
                            handleChange(
                              index,
                              "last_flight_date",
                              e.target.value
                            )
                          }
                          label="Last Flight Date"
                          error={!!errors[`${index}-last_flight_date`]}
                          helperText={errors[`${index}-last_flight_date`]}
                        />
                      </Grid>
                    </>
                  )}

                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      placeholder="Any extra notes..."
                      value={exp.additional_details}
                      onChange={(e) =>
                        handleChange(
                          index,
                          "additional_details",
                          e.target.value
                        )
                      }
                      error={!!errors[`${index}-additional_details`]}
                      helperText={errors[`${index}-additional_details`]}
                      label="Additional Details"
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          );
        })}

        <Box display="flex" justifyContent="flex-end" mt={2}>
          <Button
            variant="outlined"
            onClick={() => {
              setEntries((prev) => [...prev, createInitialExperience()]);
              setExpanded(entries.length);
            }}
          >
            Add Another
          </Button>
        </Box>

        <Box display="flex" justifyContent="space-between" alignItems="center" mt={3}>
          <Button onClick={onCancel} color="inherit" disabled={isSubmitting}>
            Cancel
          </Button>
          <Box display="flex" gap={2} alignItems="center">
            {onNext && (
              <Button
                type="button"
                variant="text"
                color="inherit"
                disabled={isSubmitting}
                onClick={onNext}
              >
                Skip to Licenses
              </Button>
            )}
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting}
              endIcon={onNext ? <ChevronRight size={18} /> : undefined}
            >
              {isSubmitting
                ? "Saving..."
                : onNext
                ? "Save & Next"
                : "Save Experience"}
            </Button>
          </Box>
        </Box>
      </LocalizationProvider>

      <DeleteDialog
        open={deleteIndex !== null}
        onClose={() => setDeleteIndex(null)}
        onConfirm={handleDelete}
        label="Experience"
      />
    </form>
  );
};

// ─── License Tab ─────────────────────────────────────────────────────────────

const LicenseTab = ({
  initial,
  onSubmit,
  onCancel,
  certificateUrl = "",
  dropDownOptions,
  onBack,
  onNext,
}) => {
  const { licenseStatusChoices } = dropDownOptions;
  const [entries, setEntries] = useState([createInitialLicense(initial)]);
  const [expanded, setExpanded] = useState(0);
  const [errors, setErrors] = useState({});
  const [fileError, setFileError] = useState("");
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [saved, setSaved] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setEntries([createInitialLicense(initial)]);
    setExpanded(0);
    setErrors({});
  }, [initial]);

  const handleChange = (index, field, value) => {
    setEntries((prev) => {
      const updated = [...prev];
      let entry = { ...updated[index], [field]: value };

      if ((field === "expiry_date" || field === "issue_date") && value) {
        const expiryStr =
          field === "expiry_date" ? value : entry.expiry_date;
        if (expiryStr) {
          const expiry = new Date(expiryStr);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          if (expiry < today) {
            entry.license_status = "expired";
          } else if (
            entry.license_status === "expired" ||
            !entry.license_status
          ) {
            entry.license_status = "active";
          }
        }
      }

      updated[index] = entry;
      return updated;
    });
    setErrors((prev) => {
      if (!prev[`${index}-${field}`]) return prev;
      const next = { ...prev };
      delete next[`${index}-${field}`];
      return next;
    });
  };

  const handleFileUpload = (index, file) => {
    setFileError("");
    if (!file) return;
    const validTypes = ["image/jpeg", "image/png", "application/pdf"];
    if (!validTypes.includes(file.type)) {
      setFileError("Please select a JPG, PNG, or PDF file");
      return false;
    }
    if (file.size > MAX_FILESIZE_MB) {
      setFileError(`File size must be less than ${SIZE_KB} MB`);
      return false;
    }
    setEntries((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], license_upload: file };
      return updated;
    });
    return true;
  };

  const validate = () => {
    const newErrors = {};
    entries.forEach((lic, i) => {
      if (!lic.license_type) newErrors[`${i}-license_type`] = "Required";
      if (!lic.license_number) newErrors[`${i}-license_number`] = "Required";
      if (!lic.license_status) newErrors[`${i}-license_status`] = "Required";
      if (!lic.issue_date) newErrors[`${i}-issue_date`] = "Required";
      if (!lic.expiry_date) newErrors[`${i}-expiry_date`] = "Required";
      if (!lic.license_upload) newErrors[`${i}-license_upload`] = "Upload required";
      if (lic.license_status === "expired" && !lic.renewal_date)
        newErrors[`${i}-renewal_date`] = "Required when status is expired";
      if (lic.issue_date?.trim() >= lic.expiry_date?.trim()) {
        newErrors[`${i}-issue_date`] =
          "Issue date cannot be greater or equal to expiry date";
      }
      if (
        lic.renewal_date?.trim() &&
        lic.renewal_date?.trim() < lic.expiry_date?.trim()
      ) {
        newErrors[`${i}-renewal_date`] =
          "Renewal date cannot be less than expiry date";
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!validate()) return;

    // Format all date fields to YYYY-MM-DD strings, handling both Date objects and string values
    const formatDate = (dateVal) => {
      if (!dateVal) return null;
      // If already a string in YYYY-MM-DD format, keep it
      if (typeof dateVal === 'string') {
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateVal)) return dateVal;
        // Otherwise try to parse and format it
        try {
          return formatDateToYMD(new Date(dateVal));
        } catch {
          return null;
        }
      }
      // If it's a Date object, format it
      if (dateVal instanceof Date && !isNaN(dateVal)) {
        return formatDateToYMD(dateVal);
      }
      return null;
    };

    const formatted = entries.map((lic) => ({
      ...lic,
      issue_date: formatDate(lic.issue_date),
      expiry_date: formatDate(lic.expiry_date),
      renewal_date: formatDate(lic.renewal_date),
      issue_authority: lic.issue_authority?.toLowerCase(),
      license_rating: lic.license_rating?.toLowerCase(),
    }));

    if (onSubmit) {
      setIsSubmitting(true);
      try {
        const shouldClose = !onNext;
        const savedData = await onSubmit({ licenses: formatted }, shouldClose);
        if (savedData) {
          if (Array.isArray(savedData)) {
            setEntries((prev) =>
              prev.map((entry, idx) => ({
                ...entry,
                id: savedData[idx]?.id || entry.id,
              }))
            );
          }
          setSaved(true);
          setTimeout(() => setSaved(false), 3000);
          if (onNext) {
            onNext();
          }
        }
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleDelete = () => {
    if (deleteIndex === null || entries.length === 1) {
      setDeleteIndex(null);
      return;
    }
    const updated = entries.filter((_, i) => i !== deleteIndex);
    setEntries(updated);
    const cleanErrors = {};
    Object.keys(errors).forEach((k) => {
      if (!k.startsWith(`${deleteIndex}-`)) cleanErrors[k] = errors[k];
    });
    setErrors(cleanErrors);
    if (expanded === deleteIndex) setExpanded(false);
    else if (expanded > deleteIndex) setExpanded(expanded - 1);
    setDeleteIndex(null);
  };

  return (
    <form onSubmit={handleSubmit}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <SavedBanner show={saved} />
        {entries.map((lic, index) => (
          <Accordion
            key={index}
            expanded={expanded === index}
            onChange={() =>
              setExpanded(expanded === index ? false : index)
            }
            sx={{ mb: 2 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                width="100%"
                pr={1}
              >
                <Typography fontWeight="bold">License {index + 1}</Typography>
                {entries.length > 1 && (
                  <IconButton
                    size="small"
                    color="error"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteIndex(index);
                    }}
                  >
                    <Trash2 size={18} />
                  </IconButton>
                )}
              </Box>
            </AccordionSummary>

            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label={
                      <span>
                        License Type <RequiredAsterisk />
                      </span>
                    }
                    fullWidth
                    value={lic.license_type}
                    onChange={(e) =>
                      handleChange(index, "license_type", e.target.value)
                    }
                    error={!!errors[`${index}-license_type`]}
                    helperText={errors[`${index}-license_type`]}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label={
                      <span>
                        License Number <RequiredAsterisk />
                      </span>
                    }
                    fullWidth
                    value={lic.license_number}
                    onChange={(e) =>
                      handleChange(index, "license_number", e.target.value)
                    }
                    error={!!errors[`${index}-license_number`]}
                    helperText={errors[`${index}-license_number`]}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Issue Authority"
                    fullWidth
                    value={lic.issue_authority}
                    onChange={(e) =>
                      handleChange(index, "issue_authority", e.target.value)
                    }
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="License Rating"
                    fullWidth
                    value={lic.license_rating}
                    onChange={(e) =>
                      handleChange(index, "license_rating", e.target.value)
                    }
                    placeholder="e.g. IR, CPL, ATPL"
                    inputProps={{ maxLength: 100 }}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <DatePicker
                    label={
                      <span>
                        Issue Date <RequiredAsterisk />
                      </span>
                    }
                    value={formatISOToDate(lic.issue_date)}
                    onChange={(date) =>
                      handleChange(index, "issue_date", formatDateToYMD(date))
                    }
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!errors[`${index}-issue_date`],
                        helperText: errors[`${index}-issue_date`],
                      },
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <DatePicker
                    label={
                      <span>
                        Expiry Date <RequiredAsterisk />
                      </span>
                    }
                    value={formatISOToDate(lic.expiry_date)}
                    onChange={(date) => {
                      handleChange(
                        index,
                        "expiry_date",
                        formatDateToYMD(date)
                      );
                      if (date) {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        if (date < today)
                          handleChange(index, "license_status", "expired");
                      }
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!errors[`${index}-expiry_date`],
                        helperText: errors[`${index}-expiry_date`],
                      },
                    }}
                  />
                </Grid>

                <Grid
                  size={{
                    xs: 12,
                    sm: lic.license_status === "expired" ? 6 : 12,
                  }}
                >
                  <Select
                    label={
                      <span>
                        License Status <RequiredAsterisk />
                      </span>
                    }
                    options={licenseStatusChoices}
                    value={lic.license_status}
                    onChange={(v) =>
                      handleChange(index, "license_status", v)
                    }
                    error={!!errors[`${index}-license_status`]}
                    helperText={errors[`${index}-license_status`]}
                  />
                </Grid>

                {lic.license_status === "expired" && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <DatePicker
                      label="Renewal Date"
                      value={formatISOToDate(lic.renewal_date)}
                      onChange={(date) =>
                        handleChange(
                          index,
                          "renewal_date",
                          formatDateToYMD(date)
                        )
                      }
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!errors[`${index}-renewal_date`],
                          helperText: errors[`${index}-renewal_date`],
                        },
                      }}
                    />
                  </Grid>
                )}

                {/* File Upload */}
                <Grid size={{ xs: 12 }}>
                  <InputLabel
                    htmlFor={`${index}-license-upload`}
                    sx={{ fontWeight: 600, mb: 0.5 }}
                  >
                    License Upload&nbsp;
                    <RequiredAsterisk />
                  </InputLabel>

                  <div
                    className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-blue-400 transition-colors duration-200"
                    onClick={() =>
                      document
                        .getElementById(`${index}-license-upload`)
                        ?.click()
                    }
                  >
                    <input
                      type="file"
                      id={`${index}-license-upload`}
                      className="hidden"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file && errors[`${index}-license_upload`]) {
                          setErrors((prev) => {
                            const next = { ...prev };
                            delete next[`${index}-license_upload`];
                            return next;
                          });
                        }
                        handleFileUpload(index, file);
                      }}
                    />

                    {lic.license_upload ? (
                      <div className="flex items-center justify-between bg-blue-50 rounded-md p-3">
                        <div className="flex items-center">
                          <FileText className="text-blue-500 mr-2" size={20} />
                          <Typography
                            component="a"
                            href={
                              typeof lic.license_upload === "string"
                                ? `${certificateUrl}${lic.license_upload}`
                                : URL.createObjectURL(lic.license_upload)
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-700 font-medium hover:underline"
                          >
                            {typeof lic.license_upload === "string"
                              ? lic.license_upload.split("/").pop()
                              : lic.license_upload.name || "Not named"}
                          </Typography>
                        </div>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            setEntries((prev) => {
                              const updated = [...prev];
                              updated[index] = {
                                ...updated[index],
                                license_upload: null,
                              };
                              return updated;
                            });
                          }}
                          size="small"
                          className="!text-red-500"
                        >
                          <X size={16} />
                        </IconButton>
                      </div>
                    ) : (
                      <>
                        <Upload
                          className="mx-auto text-gray-400 mb-2"
                          size={24}
                        />
                        <p className="text-gray-500">Upload Certificate</p>
                        <p className="text-xs text-gray-500 mt-1">
                          PDF, JPG, PNG up to {SIZE_KB}MB
                        </p>
                      </>
                    )}
                  </div>

                  {errors[`${index}-license_upload`] && (
                    <Typography variant="caption" color="error">
                      {errors[`${index}-license_upload`]}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        ))}

        <Box display="flex" justifyContent="flex-end" mt={2}>
          <Button
            variant="outlined"
            onClick={() => {
              setEntries((prev) => [...prev, createInitialLicense()]);
              setExpanded(entries.length);
            }}
          >
            Add Another
          </Button>
        </Box>

        <Box display="flex" justifyContent="space-between" alignItems="center" mt={3}>
          {onBack ? (
            <Button
              type="button"
              onClick={onBack}
              startIcon={<ChevronLeft size={18} />}
              color="inherit"
              disabled={isSubmitting}
            >
              Back: Experience
            </Button>
          ) : (
            <Button onClick={onCancel} color="inherit" disabled={isSubmitting}>
              Cancel
            </Button>
          )}
          <Box display="flex" gap={2} alignItems="center">
            {onBack && (
              <Button onClick={onCancel} color="inherit" disabled={isSubmitting}>
                Cancel
              </Button>
            )}
            {onNext && (
              <Button
                type="button"
                variant="text"
                color="inherit"
                disabled={isSubmitting}
                onClick={onNext}
              >
                Skip to Medical
              </Button>
            )}
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting}
              endIcon={onNext ? <ChevronRight size={18} /> : undefined}
            >
              {isSubmitting
                ? "Saving..."
                : onNext
                ? "Save & Next"
                : "Save License"}
            </Button>
          </Box>
        </Box>
      </LocalizationProvider>

      <Snackbar
        open={!!fileError}
        autoHideDuration={6000}
        onClose={() => setFileError("")}
      >
        <Alert severity="error">{fileError}</Alert>
      </Snackbar>

      <DeleteDialog
        open={deleteIndex !== null}
        onClose={() => setDeleteIndex(null)}
        onConfirm={handleDelete}
        label="License"
      />
    </form>
  );
};

// ─── Medical Tab ─────────────────────────────────────────────────────────────

const MedicalTab = ({ initial, onSubmit, onCancel, certificateUrl = "", dropDownOptions, onBack }) => {
  const { licenseStatusChoices } = dropDownOptions;
  const [entries, setEntries] = useState([createInitialMedical(initial)]);
  const [expanded, setExpanded] = useState(0);
  const [errors, setErrors] = useState({});
  const [fileError, setFileError] = useState("");
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [saved, setSaved] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setEntries([createInitialMedical(initial)]);
    setExpanded(0);
    setErrors({});
  }, [initial]);

  const handleChange = (index, field, value) => {
    setEntries((prev) => {
      const updated = [...prev];
      let entry = { ...updated[index], [field]: value };

      if (
        (field === "medical_expiry_date" || field === "medical_issue_date") &&
        value
      ) {
        const expiryStr =
          field === "medical_expiry_date" ? value : entry.medical_expiry_date;
        if (expiryStr) {
          const expiry = new Date(expiryStr);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          if (expiry < today) {
            entry.medical_status = "expired";
          } else if (
            entry.medical_status === "expired" ||
            !entry.medical_status
          ) {
            entry.medical_status = "active";
          }
        }
      }

      updated[index] = entry;
      return updated;
    });
    setErrors((prev) => {
      if (!prev[`${index}-${field}`]) return prev;
      const next = { ...prev };
      delete next[`${index}-${field}`];
      return next;
    });
  };

  const handleFileUpload = (index, file) => {
    setFileError("");
    if (!file) return;
    const validTypes = ["image/jpeg", "image/png", "application/pdf"];
    if (!validTypes.includes(file.type)) {
      setFileError("Please select a JPG, PNG, or PDF file");
      return false;
    }
    if (file.size > MAX_FILESIZE_MB) {
      setFileError(`File size must be less than ${SIZE_KB} MB`);
      return false;
    }
    setEntries((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], medical_upload: file };
      return updated;
    });
    return true;
  };

  const validate = () => {
    const newErrors = {};
    entries.forEach((med, i) => {
      if (!med.medical_certificate_number)
        newErrors[`${i}-medical_certificate_number`] = "Required";
      if (!med.medical_status)
        newErrors[`${i}-medical_status`] = "Required";
      if (!med.medical_issue_date)
        newErrors[`${i}-medical_issue_date`] = "Required";
      if (!med.medical_expiry_date)
        newErrors[`${i}-medical_expiry_date`] = "Required";
      if (!med.medical_upload)
        newErrors[`${i}-medical_upload`] = "Upload required";
      if (med.medical_status === "expired" && !med.medical_renewal_date)
        newErrors[`${i}-medical_renewal_date`] = "Required when status is expired";
      if (
        med.medical_issue_date &&
        med.medical_expiry_date &&
        med.medical_issue_date.trim() >= med.medical_expiry_date.trim()
      ) {
        newErrors[`${i}-medical_issue_date`] =
          "Issue date cannot be greater or equal to expiry date";
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!validate()) return;
    // Format all date fields to YYYY-MM-DD strings, handling both Date objects and string values
    const formatted = entries.map((med) => {
      const formatDate = (dateVal) => {
        if (!dateVal) return null;
        // If already a string in YYYY-MM-DD format, keep it
        if (typeof dateVal === 'string') {
          if (/^\d{4}-\d{2}-\d{2}$/.test(dateVal)) return dateVal;
          // Otherwise try to parse and format it
          try {
            return formatDateToYMD(new Date(dateVal));
          } catch {
            return null;
          }
        }
        // If it's a Date object, format it
        if (dateVal instanceof Date && !isNaN(dateVal)) {
          return formatDateToYMD(dateVal);
        }
        return null;
      };

      return {
        ...med,
        medical_issue_date: formatDate(med.medical_issue_date),
        medical_expiry_date: formatDate(med.medical_expiry_date),
        medical_renewal_date: formatDate(med.medical_renewal_date),
      };
    });

    if (onSubmit) {
      setIsSubmitting(true);
      try {
        const savedData = await onSubmit({ medicals: formatted }, true);
        if (savedData) {
          setSaved(true);
        }
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleDelete = () => {
    if (deleteIndex === null || entries.length === 1) {
      setDeleteIndex(null);
      return;
    }
    const updated = entries.filter((_, i) => i !== deleteIndex);
    setEntries(updated);
    const cleanErrors = {};
    Object.keys(errors).forEach((k) => {
      if (!k.startsWith(`${deleteIndex}-`)) cleanErrors[k] = errors[k];
    });
    setErrors(cleanErrors);
    if (expanded === deleteIndex) setExpanded(false);
    else if (expanded > deleteIndex) setExpanded(expanded - 1);
    setDeleteIndex(null);
  };

  return (
    <form onSubmit={handleSubmit}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <SavedBanner show={saved} />
        {entries.map((med, index) => (
          <Accordion
            key={index}
            expanded={expanded === index}
            onChange={() =>
              setExpanded(expanded === index ? false : index)
            }
            sx={{ mb: 2 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                width="100%"
                pr={1}
              >
                <Typography fontWeight="bold">Medical {index + 1}</Typography>
                {entries.length > 1 && (
                  <IconButton
                    size="small"
                    color="error"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteIndex(index);
                    }}
                  >
                    <Trash2 size={18} />
                  </IconButton>
                )}
              </Box>
            </AccordionSummary>

            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Medical Class"
                    fullWidth
                    value={med.medical_class}
                    onChange={(e) =>
                      handleChange(index, "medical_class", e.target.value)
                    }
                    placeholder="e.g. Class 1, Class 2"
                    error={!!errors[`${index}-medical_class`]}
                    helperText={errors[`${index}-medical_class`]}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label={
                      <span>
                        Medical Certificate Number <RequiredAsterisk />
                      </span>
                    }
                    fullWidth
                    value={med.medical_certificate_number}
                    onChange={(e) => {
                      // Allow alphanumeric only (letters + digits)
                      const val = e.target.value.replace(/[^a-zA-Z0-9\-\/\s]/g, "");
                      handleChange(index, "medical_certificate_number", val);
                    }}
                    inputProps={{ maxLength: 50 }}
                    placeholder="e.g. MC-12345"
                    error={!!errors[`${index}-medical_certificate_number`]}
                    helperText={errors[`${index}-medical_certificate_number`]}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <DatePicker
                    label={
                      <span>
                        Medical Issue Date <RequiredAsterisk />
                      </span>
                    }
                    value={formatISOToDate(med.medical_issue_date)}
                    onChange={(date) =>
                      handleChange(
                        index,
                        "medical_issue_date",
                        formatDateToYMD(date)
                      )
                    }
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!errors[`${index}-medical_issue_date`],
                        helperText: errors[`${index}-medical_issue_date`],
                      },
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <DatePicker
                    label={
                      <span>
                        Medical Expiry Date <RequiredAsterisk />
                      </span>
                    }
                    value={formatISOToDate(med.medical_expiry_date)}
                    onChange={(date) => {
                      handleChange(
                        index,
                        "medical_expiry_date",
                        formatDateToYMD(date)
                      );
                      if (date) {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        if (date < today)
                          handleChange(index, "medical_status", "expired");
                      }
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!errors[`${index}-medical_expiry_date`],
                        helperText: errors[`${index}-medical_expiry_date`],
                      },
                    }}
                  />
                </Grid>

                <Grid
                  size={{
                    xs: 12,
                    sm: med.medical_status === "expired" ? 6 : 12,
                  }}
                >
                  <Select
                    label={
                      <span>
                        Medical Status <RequiredAsterisk />
                      </span>
                    }
                    options={licenseStatusChoices}
                    value={med.medical_status}
                    onChange={(v) =>
                      handleChange(index, "medical_status", v)
                    }
                    error={!!errors[`${index}-medical_status`]}
                    helperText={errors[`${index}-medical_status`]}
                  />
                </Grid>

                {med.medical_status === "expired" && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <DatePicker
                      label="Medical Renewal Date"
                      value={formatISOToDate(med.medical_renewal_date)}
                      onChange={(date) =>
                        handleChange(
                          index,
                          "medical_renewal_date",
                          formatDateToYMD(date)
                        )
                      }
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!errors[`${index}-medical_renewal_date`],
                          helperText: errors[`${index}-medical_renewal_date`],
                        },
                      }}
                    />
                  </Grid>
                )}

                {/* File Upload */}
                <Grid size={{ xs: 12 }}>
                  <InputLabel
                    htmlFor={`${index}-medical-upload`}
                    sx={{ fontWeight: 600, mb: 0.5 }}
                  >
                    Medical Certificate Upload&nbsp;
                    <RequiredAsterisk />
                  </InputLabel>

                  <div
                    className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-blue-400 transition-colors duration-200"
                    onClick={() =>
                      document
                        .getElementById(`${index}-medical-upload`)
                        ?.click()
                    }
                  >
                    <input
                      type="file"
                      id={`${index}-medical-upload`}
                      className="hidden"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file && errors[`${index}-medical_upload`]) {
                          setErrors((prev) => {
                            const next = { ...prev };
                            delete next[`${index}-medical_upload`];
                            return next;
                          });
                        }
                        handleFileUpload(index, file);
                      }}
                    />

                    {med.medical_upload ? (
                      <div className="flex items-center justify-between bg-blue-50 rounded-md p-3">
                        <div className="flex items-center">
                          <FileText className="text-blue-500 mr-2" size={20} />
                          <Typography
                            component="a"
                            href={
                              typeof med.medical_upload === "string"
                                ? `${certificateUrl}${med.medical_upload}`
                                : URL.createObjectURL(med.medical_upload)
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-700 font-medium hover:underline"
                          >
                            {typeof med.medical_upload === "string"
                              ? med.medical_upload.split("/").pop()
                              : med.medical_upload.name || "Not named"}
                          </Typography>
                        </div>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            setEntries((prev) => {
                              const updated = [...prev];
                              updated[index] = {
                                ...updated[index],
                                medical_upload: null,
                              };
                              return updated;
                            });
                          }}
                          size="small"
                          className="!text-red-500"
                        >
                          <X size={16} />
                        </IconButton>
                      </div>
                    ) : (
                      <>
                        <Upload
                          className="mx-auto text-gray-400 mb-2"
                          size={24}
                        />
                        <p className="text-gray-500">Upload Certificate</p>
                        <p className="text-xs text-gray-500 mt-1">
                          PDF, JPG, PNG up to {SIZE_KB}MB
                        </p>
                      </>
                    )}
                  </div>

                  {errors[`${index}-medical_upload`] && (
                    <Typography variant="caption" color="error">
                      {errors[`${index}-medical_upload`]}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        ))}

        <Box display="flex" justifyContent="flex-end" mt={2}>
          <Button
            variant="outlined"
            onClick={() => {
              setEntries((prev) => [...prev, createInitialMedical()]);
              setExpanded(entries.length);
            }}
          >
            Add Another
          </Button>
        </Box>

        <Box display="flex" justifyContent="space-between" alignItems="center" mt={3}>
          {onBack ? (
            <Button
              type="button"
              onClick={onBack}
              startIcon={<ChevronLeft size={18} />}
              color="inherit"
              disabled={isSubmitting}
            >
              Back: Licenses
            </Button>
          ) : (
            <Button onClick={onCancel} color="inherit" disabled={isSubmitting}>
              Cancel
            </Button>
          )}
          <Box display="flex" gap={2} alignItems="center">
            {onBack && (
              <Button onClick={onCancel} color="inherit" disabled={isSubmitting}>
                Cancel
              </Button>
            )}
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Saving..." : "Save Medical"}
            </Button>
          </Box>
        </Box>
      </LocalizationProvider>

      <Snackbar
        open={!!fileError}
        autoHideDuration={6000}
        onClose={() => setFileError("")}
      >
        <Alert severity="error">{fileError}</Alert>
      </Snackbar>

      <DeleteDialog
        open={deleteIndex !== null}
        onClose={() => setDeleteIndex(null)}
        onConfirm={handleDelete}
        label="Medical"
      />
    </form>
  );
};


const TAB_CONFIG = [
  { label: "Experience", icon: <Plane size={16} /> },
  { label: "Licenses", icon: <BadgeCheck size={16} /> },
  { label: "Medical", icon: <HeartPulse size={16} /> },
];

const ExperienceForm = ({
  experience,
  onSubmit,
  onCancel,
  certificateUrl = "",
  dropDownOptions,
}) => {
  const isEditMode = !!experience; // If experience exists, it's edit mode

  // For edit mode, determine which single form to show
  const getEditFormType = () => {
    if (!experience) return "experience";
    
    // Check if it's a medical record
    if (experience.medical_certificate_number || experience.medical_class) {
      return "medical";
    }
    
    // Check if it's a license record
    if (experience.license_number) {
      return "license";
    }
    
    return "experience";
  };

  const [activeTab, setActiveTab] = useState(0);
  const editFormType = isEditMode ? getEditFormType() : null;

  /**
   * onSubmit is called with a keyed payload:
   *   { experience: [...] }  |  { licenses: [...] }  |  { medicals: [...] }
   */

  // EDIT MODE: Show only the relevant form without tabs
  if (isEditMode) {
    return (
      <Box>
        {editFormType === "experience" && (
          <ExperienceTab
            initial={experience}
            onSubmit={onSubmit}
            onCancel={onCancel}
            dropDownOptions={dropDownOptions}
          />
        )}

        {editFormType === "license" && (
          <LicenseTab
            initial={experience}
            onSubmit={onSubmit}
            onCancel={onCancel}
            certificateUrl={certificateUrl}
            dropDownOptions={dropDownOptions}
          />
        )}

        {editFormType === "medical" && (
          <MedicalTab
            initial={experience}
            onSubmit={onSubmit}
            onCancel={onCancel}
            certificateUrl={certificateUrl}
            dropDownOptions={dropDownOptions}
          />
        )}
      </Box>
    );
  }

  // ADD MODE: Show all tabs with tab navigation
  return (
    <Box>
      <Box sx={{ width: "100%", mb: 3, pt: 1, pb: 1.5, borderBottom: 1, borderColor: "divider" }}>
        <Stepper activeStep={activeTab} nonLinear sx={{ px: { xs: 0, sm: 1 } }}>
          {TAB_CONFIG.map(({ label, icon }, index) => (
            <Step key={label}>
              <StepButton
                onClick={() => setActiveTab(index)}
                sx={{
                  py: 0.5,
                  borderRadius: 2,
                  "&:hover": {
                    bgcolor: "action.hover",
                  },
                }}
              >
                <StepLabel
                  icon={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                        backgroundColor:
                          activeTab === index ? "primary.main" : "action.hover",
                        color:
                          activeTab === index
                            ? "primary.contrastText"
                            : "text.secondary",
                        border: "1px solid",
                        borderColor:
                          activeTab === index ? "primary.main" : "divider",
                        transition: "all 0.2s ease-in-out",
                      }}
                    >
                      {icon}
                    </Box>
                  }
                  sx={{
                    "& .MuiStepLabel-label": {
                      fontWeight: activeTab === index ? 700 : 500,
                      color:
                        activeTab === index ? "primary.main" : "text.secondary",
                      fontSize: "0.9rem",
                    },
                  }}
                >
                  {label}
                </StepLabel>
              </StepButton>
            </Step>
          ))}
        </Stepper>
      </Box>

      <TabPanel value={activeTab} index={0}>
        <ExperienceTab
          initial={experience}
          onSubmit={onSubmit}
          onCancel={onCancel}
          dropDownOptions={dropDownOptions}
          onNext={() => setActiveTab(1)}
        />
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <LicenseTab
          initial={experience}
          onSubmit={onSubmit}
          onCancel={onCancel}
          certificateUrl={certificateUrl}
          dropDownOptions={dropDownOptions}
          onBack={() => setActiveTab(0)}
          onNext={() => setActiveTab(2)}
        />
      </TabPanel>

      <TabPanel value={activeTab} index={2}>
        <MedicalTab
          initial={experience}
          onSubmit={onSubmit}
          onCancel={onCancel}
          certificateUrl={certificateUrl}
          dropDownOptions={dropDownOptions}
          onBack={() => setActiveTab(1)}
        />
      </TabPanel>
    </Box>
  );
};

export default ExperienceForm;